import { HeadCell } from 'shell/components/shared/table/types';
import {
  Table as TableMaterial,
  TableContainer,
  TableBody,
  TableRow,
  TableHead,
  Checkbox,
  TableCellProps,
  TableCell,
} from '@mui/material';
import { PmiTableRow } from 'shell/components/shared/table/row';
import { PmiHeadTableCell } from 'shell/components/shared/table-custom/head';
import { memo, useCallback, useMemo, useState } from 'react';
import { CheckboxTable } from 'shell/view-models/checkbox-table';
import { styled } from '@mui/system';

export const PmiHeadTableCellCheckbox = styled((props: TableCellProps) => (
  <PmiHeadTableCell {...props} />
))(() => ({
  padding: '4px 12px !important',
}));
interface CheckboxTableProps<T extends CheckboxTable, K extends keyof T> {
  onCheckPress: (cell: T) => void;
  onSelectAll?: (selected: boolean) => void;
  body: Array<T>;
  head: Array<HeadCell<T, K>>;
  disabled?: boolean;
  insert?: boolean;
  minWidth?: number;
}

const TableOperatorServicesComponent = <T extends CheckboxTable, K extends keyof T>({
  onCheckPress,
  body,
  disabled,
  // insert,
  minWidth,
  head,
  onSelectAll,
}: CheckboxTableProps<T, K>): React.ReactElement => {
  const [selectAll, setSelectAll] = useState<boolean>(false);

  const numSelected = useMemo(() => {
    return body.reduce((prev, next) => {
      return next && next?.checked ? prev + 1 : prev;
    }, 0);
  }, [selectAll, body]);

  const handleSelectAll = useCallback(() => {
    return new Promise((resolve) => resolve(setSelectAll(!selectAll))).then(() => {
      if (onSelectAll) {
        onSelectAll(!selectAll);
      }
    });
  }, [setSelectAll, onSelectAll, selectAll]);

  return (
    <TableContainer sx={{ maxHeight: 440 }}>
      <TableMaterial
        sx={{ minWidth: minWidth || 1 }}
        size="medium"
        stickyHeader
        aria-label="Lista servizi"
      >
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                id="selectAll"
                name="selectAll"
                indeterminate={numSelected > 0 && numSelected < body.length}
                checked={body.length > 0 && numSelected === body.length}
                onChange={handleSelectAll}
                disabled={disabled || !onSelectAll}
                inputProps={{ 'aria-label': 'Checkbox Attivo' }}
              />
            </TableCell>
            {head.map((headCell) => {
              return headCell.label ? (
                <TableCell key={`headCell-${headCell.id.toString()}`} align="left" padding="none">
                  {headCell.label}
                </TableCell>
              ) : undefined;
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {body.map((row, bodyItemIndex: number) => {
            const id = `table-row-${bodyItemIndex}`;

            return (
              <PmiTableRow
                key={`TableRow-${id.toString()}`}
                hover={!disabled}
                onClick={() => {
                  if (!disabled) {
                    onCheckPress(row);
                  }
                }}
                role="checkbox"
                aria-checked={row.checked}
                tabIndex={-1}
                selected={row.checked}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    id="check"
                    name="check"
                    checked={row.checked}
                    disabled={disabled}
                    onChange={() => onCheckPress(row)}
                    inputProps={{ 'aria-label': `attiva ${bodyItemIndex}` }}
                  />
                </TableCell>
                {head.map((column, headItemIndex) => {
                  const labelId = `table-${row[column.id]}-${headItemIndex}`;
                  if (column.label) {
                    return (
                      <TableCell
                        key={`TableCell-${labelId.toString()}`}
                        id={labelId}
                        scope="row"
                        padding="none"
                        align="left"
                      >
                        {row[column.id]}
                      </TableCell>
                    );
                  }
                  return null;
                })}
              </PmiTableRow>
            );
          })}
        </TableBody>
      </TableMaterial>
    </TableContainer>
  );
};
TableOperatorServicesComponent.displayName = 'Table Operator Services component';

const TableOperatorServices = memo(TableOperatorServicesComponent);

export default TableOperatorServices as typeof TableOperatorServicesComponent;
